import { useTranslation } from "react-i18next";
import { Tabs } from "flowbite-react";

import { Sidenav } from "../../components";
import { ProfileSettings } from "./ProfileSettings/ProfileSettings";
import { TeamSettings } from "./TeamSettings/TeamSettings";
import { ActionsSettings } from "./ActionsSettings";
import { SignalsPlaybooksSettings } from "./SignalsPlaybooks";
import { SecuritySettings } from "./SecuritySettings";
import { useFeatureFlag, FEATURE_FLAGS } from "../../hooks";
import { useSearchParams } from "react-router-dom";
import { useRef } from "react";

export const SettingsPage = () => {
  const { t } = useTranslation();
  const signalsPlaybooksEnabled = useFeatureFlag(
    FEATURE_FLAGS.SIGNAL_ACTION_PLAYBOOKS,
  );

  const [searchParams, setSearchParams] = useSearchParams();
  const tabsRef = useRef(null);

  // Tabs.Item children are indexed positionally by flowbite-react's <Tabs>,
  // so gating a tab behind a feature flag must filter this array (not
  // conditionally render a Tabs.Item) — otherwise the numeric indices used
  // by onActiveTabChange/activeTab would drift out of sync with what's shown.
  const ALL_TABS = [
    {
      key: "team",
      title: t("pages.settings.tabs.teamSettings"),
      render: () => <TeamSettings />,
    },
    {
      key: "profile",
      title: t("pages.settings.tabs.profileSettings"),
      render: () => <ProfileSettings />,
    },
    {
      key: "actions",
      title: t("pages.settings.tabs.actionsSettings"),
      render: () => <ActionsSettings />,
    },
    signalsPlaybooksEnabled && {
      key: "signalsPlaybooks",
      title: t("pages.settings.tabs.signalsPlaybooks"),
      render: () => <SignalsPlaybooksSettings />,
    },
    {
      key: "security",
      title: t("pages.settings.tabs.security"),
      render: () => <SecuritySettings />,
    },
  ].filter(Boolean);

  const TABS = ALL_TABS.map((tab) => tab.key);

  const activeTab =
    TABS.find((tab) => tab === searchParams.get("tab")) || TABS[0];

  const updateURLParams = (newParams) => {
    const currentParams = new URLSearchParams(searchParams);

    Object.entries(newParams).forEach(([key, value]) => {
      if (value !== null && value !== undefined && value !== "") {
        currentParams.set(key, value);
      } else {
        currentParams.delete(key);
      }
    });

    setSearchParams(currentParams, { replace: true });
  };

  const handleTabChange = (tabIdx) => {
    updateURLParams({ tab: TABS[tabIdx] });
  };

  return (
    <div className="flex min-h-full flex-1 flex-row">
      <Sidenav />
      <div className="flex h-screen flex-1 flex-col overflow-y-scroll">
        <div className="animation-slide-up-lil-bounce m-5 flex flex-1 flex-col px-1">
          <div className="mb-4 text-4xl font-bold leading-10 text-black">
            {t("pages.settings.title")}
          </div>

          <Tabs
            aria-label="Tabs with underline"
            variant="underline"
            className="gap-2"
            ref={tabsRef}
            onActiveTabChange={(tab) => handleTabChange(tab)}
          >
            {ALL_TABS.map((tab) => (
              <Tabs.Item
                key={tab.key}
                active={activeTab === tab.key}
                title={tab.title}
              >
                {tab.render()}
              </Tabs.Item>
            ))}
          </Tabs>
        </div>
      </div>
    </div>
  );
};
